import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { computeTotalCost } from '~/features/billing/utils'
import { useBilling, PlanAddOn } from '~/features/usage/useBilling'
import { useMonthlyCheckout, useQueryKeys } from '~/hooks'
import { trackEvent } from '~/providers'
import { removePlansFromProductMap } from '~/features/billing/helpers'
import { Button, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
import { AddOns } from '~/features/billing/componentsV2/AddOns'
import { dollars } from '~/utils'
import { Loader2 } from 'lucide-react'

export const Route = createFileRoute('/workspaces/$workspaceId/billing/add-ons')({
  component: () => {
    return <AddOnsSection />
  },
})

function AddOnsSection() {
  const { workspaceId } = Route.useParams()
  const [hasPendingAddonChanges, setHasPendingAddonChanges] = useState(false)
  const [draftAddons, setDraftAddons] = useState<PlanAddOn[]>([])

  const { initialProductQuantityMap, productQuantityMap, plan, promoCode, nextMonthAddOns, ...billingProps } =
    useBilling(workspaceId)
  const billingReadonly = billingProps.billingInfo?.billingReadonly

  const addOnsInitialProductMap = removePlansFromProductMap(initialProductQuantityMap)
  const addOnsProductMap = removePlansFromProductMap(productQuantityMap)

  const { queryClient } = Route.useRouteContext()

  // Convert draftAddons back to productQuantityMap for checkout
  const updatedProductQuantityMap = useMemo(() => {
    const newProductQuantityMap = { ...productQuantityMap }

    Object.keys(addOnsProductMap).forEach((productId) => {
      if (newProductQuantityMap[productId]) {
        newProductQuantityMap[productId].quantity = 0
      }
    })

    draftAddons.forEach((addon) => {
      if (newProductQuantityMap[addon.productId]) {
        newProductQuantityMap[addon.productId]!.quantity = addon.quantity
      }
    })

    return newProductQuantityMap
  }, [productQuantityMap, addOnsProductMap, draftAddons])

  const initialTotalCost = useMemo(
    () => computeTotalCost(addOnsInitialProductMap, promoCode),
    [addOnsInitialProductMap, promoCode]
  )
  const totalCost = useMemo(
    () => computeTotalCost(removePlansFromProductMap(updatedProductQuantityMap), promoCode),
    [updatedProductQuantityMap, promoCode]
  )

  const totalDiscount = useMemo(() => {
    if (!promoCode?.coupon.appliesTo) return 0

    const updatedAddOnsMap = removePlansFromProductMap(updatedProductQuantityMap)
    let totalDiscountAmount = 0

    Object.entries(updatedAddOnsMap).forEach(([productId, { quantity, productDetails }]) => {
      if (promoCode.coupon.appliesTo.includes(productId) && quantity > 0) {
        const priceCents = productDetails.price.unit_amount ?? 0
        const discountAmount = ((priceCents * promoCode.coupon.percentOff) / 100) * quantity
        totalDiscountAmount += discountAmount / 100
      }
    })

    return totalDiscountAmount
  }, [updatedProductQuantityMap, promoCode])

  const { mutate: handleCheckout, isPending } = useMonthlyCheckout(workspaceId, {
    productQuantityMap: updatedProductQuantityMap,
    initialProductQuantityMap,
    promoCode,
    ...billingProps,
  })

  const handleUpdateSubscription = async () => {
    if (!hasPendingAddonChanges) return

    const changes: string[] = []

    draftAddons.forEach((addon) => {
      const savedAddon = nextMonthAddOns?.find((a) => a.productId === addon.productId)
      const savedQuantity = savedAddon?.quantity || 0

      if (addon.quantity !== savedQuantity) {
        const addonName = addon.name.replace('Add-on - ', '')
        if (addon.quantity > savedQuantity) {
          changes.push(`Add ${addon.quantity - savedQuantity} ${addonName} (${addon.quantity} total)`)
        } else {
          changes.push(`Remove ${savedQuantity - addon.quantity} ${addonName} (${addon.quantity} total)`)
        }
      }
    })

    if (changes.length === 0) return

    void showConfirmationPrompt(
      <div className="space-y-2">
        <p>The following changes will be applied to your subscription:</p>
        <ul className="list-disc space-y-1 pl-4">
          {changes.map((change, index) => (
            <li key={index} className="text-sm">
              {change}
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">
          Your add-ons will cost <strong>${dollars(totalCost)}</strong> per month. Are you sure you want to proceed?
        </p>
      </div>,
      {
        title: 'Confirm Subscription Changes',
        confirmLabel: 'Update Subscription',
        cancelLabel: 'Cancel',
      }
    ).then(() => {
      trackEvent({
        type: 'update_addons',
        state: 'confirmed',
        upgrade: totalCost > initialTotalCost,
        workspaceId,
        plan,
      })
      //TODO: This is a workaround to invalidate the cache when the user changes the plan in the stripe customer portal
      void queryClient.invalidateQueries({ queryKey: useQueryKeys().getAllWorkspaceUsages(workspaceId) })
      handleCheckout()
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <AddOns
        workspaceId={workspaceId}
        setHasPendingAddonChanges={setHasPendingAddonChanges}
        draftAddons={draftAddons}
        setDraftAddons={setDraftAddons}
      />

      <div className="flex flex-col justify-between gap-2 md:flex-row">
        <div className="flex flex-col gap-1">
          {totalDiscount > 0 && <p className="font-medium text-muted-foreground">Total Discount</p>}
          <p className="font-medium">Total Add-ons cost per month</p>
        </div>
        <div className="flex flex-col gap-1 md:items-end">
          {totalDiscount > 0 && <p className="text-sm text-lime-500">-${dollars(totalDiscount)}</p>}
          <div className="flex gap-2">
            {initialTotalCost !== totalCost && (
              <p className="text-muted-foreground line-through opacity-60">${dollars(initialTotalCost)}</p>
            )}
            <p className="font-medium">${dollars(totalCost)}</p>
          </div>
        </div>
      </div>

      <Button
        className="ml-auto w-fit"
        disabled={!hasPendingAddonChanges || billingReadonly || isPending}
        onClick={() => {
          trackEvent({
            type: 'update_addons',
            state: 'clicked',
            upgrade: totalCost > initialTotalCost,
            workspaceId,
            plan,
          })
          handleUpdateSubscription()
        }}
      >
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <p>Update plan</p>}
      </Button>
    </div>
  )
}
